import type { BrandDna, BrandDnaPatch } from '@studio/contract';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useStudioClient } from '../context';
import { parseApiError } from '../errors';

const QUERY_KEY = ['brand-dna'] as const;

/**
 * `GET /api/brand-dna` — workspace's BrandDna or BRAND_DNA_DEFAULT when none.
 */
export function useBrandDna() {
  const client = useStudioClient();
  return useQuery({
    queryKey: QUERY_KEY,
    queryFn: async () => {
      const res = await client.api['brand-dna'].$get();
      if (!res.ok) throw await parseApiError(res);
      return res.json();
    },
  });
}

/**
 * `PATCH /api/brand-dna` — partial update; each top-level section is fully
 * replaced if present, untouched if absent. Invalidates the GET cache so
 * the form re-renders with the server-merged shape (incl. `updatedAt`).
 */
export function useUpdateBrandDna() {
  const client = useStudioClient();
  const qc = useQueryClient();
  return useMutation<BrandDna, Error, BrandDnaPatch>({
    mutationFn: async (patch) => {
      const res = await client.api['brand-dna'].$patch({ json: patch });
      if (!res.ok) throw await parseApiError(res);
      return res.json();
    },
    onSuccess: (data) => {
      qc.setQueryData(QUERY_KEY, data);
    },
  });
}
